/**
 * The one query key for GET /api/events/live, carrying this device's timezone.
 *
 * WHY THE ZONE IS IN THE URL. Almost every Pokémon GO event is a WALL-CLOCK
 * time — a Community Day runs "2pm to 5pm local" — so the same event is on at a
 * different instant for every trainer. The server splits the feed into
 * active / upcoming / recent, and it cannot do that correctly without knowing
 * where the caller is.
 *
 * It got this wrong in the obvious direction: the Nickit Community Day of
 * 2026-08-16 ended at 5pm, and at UTC+10 the server did not move it out of
 * `active` until 3am the following day, so the post-event catch-up prompt
 * arrived a day late. West of UTC the same bug fires the prompt DURING the
 * event.
 *
 * Four surfaces query this endpoint and every one of them must send the same
 * key, or React Query caches the feed twice and two parts of the same screen
 * disagree about what is on. Hence one module.
 *
 * See docs/event-time-zones.md and cloudflare/src/lib/eventTime.ts.
 */

import { attendingIds } from "./eventAttending";

/** This device's IANA zone, or null when the runtime will not say. */
export function deviceTimeZone(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof tz === "string" && tz.length > 0 ? tz : null;
  } catch {
    return null;
  }
}

/**
 * The query key. A device that cannot report a zone falls back to the bare
 * path, which the server buckets in UTC — the behaviour of every build before
 * this one, rather than a guess.
 *
 * The device's attended one-place events ride along as `attending=`
 * (lib/eventAttending, 2026-10-23): the server keeps a City Safari out of
 * every bucket unless its id is in that list, so flipping the Calendar's
 * Attending switch changes the key and every surface refetches the feed
 * with the event in or out. Sorted, so the key is stable for one state.
 */
export function eventsLiveKey(): string {
  const tz = deviceTimeZone();
  const params: string[] = [];
  if (tz) params.push(`tz=${encodeURIComponent(tz)}`);
  const attending = attendingIds();
  if (attending.length > 0) params.push(`attending=${attending.map(encodeURIComponent).join(",")}`);
  return params.length > 0 ? `/api/events/live?${params.join("&")}` : "/api/events/live";
}
